import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Cartao } from '../../componentes/base';
import { Estado, Vazio } from '../../componentes/estados';
import { get } from '../../nucleo/api';
import { Barra, BotaoExportar, LinhaEscopo, Rolagem, Segmentado, TD, TH, useParametro, usePeriodo, Valor } from './comum';
import { SeletorIntercompany, useIntercompany } from './Dre';
import { BASES, COLUNAS_CENTRO, type Base, type Centros as CentrosApi, type LinhaCentro } from './tipos';

/** R9 · Resultado por centro de custo, com o balde "Sem centro" no total. */
export function Centros() {
  const { t } = useTranslation('relatorios');
  const { periodo, seletor } = usePeriodo();
  const [base, setBase] = useParametro<Base>('base', 'competencia', BASES);
  const [inter, setInter, interVigente] = useIntercompany();
  const params = { de: periodo.de, ate: periodo.ate, base, intercompany: inter };
  const q = useQuery({ queryKey: ['/relatorios/centros', params], queryFn: () => get<CentrosApi>('/relatorios/centros', params), placeholderData: keepPreviousData });

  const linha = (c: LinhaCentro, moeda: string, total = false) => (
    <tr key={c.centro_custo_id ?? (total ? 'total' : 'sem')} className={`border-b border-borda last:border-0 ${total ? 'bg-sup-2 font-semibold' : ''}`}>
      <th scope="row" className={`${TD} max-w-56 text-left ${total ? '' : 'font-normal'} break-words`}>
        {total ? t('comum.total') : c.centro_custo_id ? `${c.codigo ? `${c.codigo} ` : ''}${c.nome ?? ''}` : t('comum.semCentro')}
      </th>
      {COLUNAS_CENTRO.map((k) => (
        <td key={k} className={`${TD} text-right`}>
          <Valor centavos={c.valores[k]} moeda={moeda} />
        </td>
      ))}
      <td className={`${TD} text-right font-semibold`}>
        <Valor centavos={c.resultado_centavos} moeda={moeda} />
      </td>
      <td className={`${TD} text-right text-tinta-2`}>
        <Valor centavos={c.fora_dre_centavos} moeda={moeda} />
      </td>
    </tr>
  );

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <Barra>
        {seletor}
        <Segmentado
          rotulo={t('comum.baseRotulo')}
          valor={base}
          aoMudar={setBase}
          opcoes={[
            { valor: 'competencia', rotulo: t('comum.base.competencia') },
            { valor: 'caixa', rotulo: t('comum.base.caixa') },
          ]}
        />
        {q.data?.escopo.modo === 'consolidado' && <SeletorIntercompany valor={interVigente} aoMudar={setInter} />}
        <BotaoExportar relatorio="centros" parametros={params} />
      </Barra>
      <Estado consulta={q}>
        {(d) => (
          <>
            <LinhaEscopo escopo={d.escopo} />
            {d.por_moeda.length === 0 ? (
              <Cartao>
                <Vazio titulo={t('centros.vazioTitulo')} texto={t('centros.vazioTexto')} />
              </Cartao>
            ) : (
              d.por_moeda.map((m) => (
                <Cartao key={m.moeda} titulo={d.por_moeda.length > 1 ? t('comum.moeda', { moeda: m.moeda }) : undefined} semPadding>
                  <Rolagem rotulo={`${t('comum.tabela')} (${m.moeda})`}>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-borda text-left text-tinta-3">
                          <th scope="col" className={TH}>{t('centros.centro')}</th>
                          {COLUNAS_CENTRO.map((k) => (
                            <th key={k} scope="col" className={`${TH} text-right`}>{t(`centros.colunas.${k}`)}</th>
                          ))}
                          <th scope="col" className={`${TH} text-right`}>{t('centros.resultado')}</th>
                          <th scope="col" className={`${TH} text-right`}>{t('centros.foraDre')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {m.centros.map((c) => linha(c, m.moeda))}
                        {linha(m.total, m.moeda, true)}
                      </tbody>
                    </table>
                  </Rolagem>
                  <p className="border-t border-borda px-4 py-3 text-xs text-tinta-3">{t('centros.outrosAjuda')}</p>
                </Cartao>
              ))
            )}
          </>
        )}
      </Estado>
    </div>
  );
}
